import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { signOut } from 'next-auth/react';

import { getUserAccountDetails } from '@/services/webservices';

type UseSessionProps = {
	required?: boolean;
	redirectTo?: string;
	queryConfig?: any;
};

type UseSessionExport = {
	session: any;
	isLoading: boolean;
	refetchSession: Function;
	logOut: Function;
};

export async function fetchSession() {
	const res = await fetch( '/api/auth/session' );
	const session = await res.json();

	if ( Object.keys( session ).length ) {
		let contactInfo = '';

		if ( session?.user?.id ) {
			const accountDetails = await getUserAccountDetails( session?.user?.id );

			if ( accountDetails ) {
				contactInfo = accountDetails.contactInfo || null;
			}
		}

		return {
			...session,
			user: {
				...session.user,
				contactInfo
			}
		};
	}

	return null;
}

export function useSession({
	required,
	redirectTo = '/api/auth/signin?error=SessionExpired',
	queryConfig = {}
}: UseSessionProps = {} ): UseSessionExport {
	const router = useRouter();
	const { data: session, isLoading, refetch } = useQuery( [ 'session' ], fetchSession, {
		...queryConfig,
		onSettled( data, error ) {
			if ( queryConfig.onSettled ) {
				queryConfig.onSettled( data, error );
			}

			if ( data || !required ) {
				return;
			}

			router.push( redirectTo );
		}
	});

	const logOut = useCallback( async ()=> {
		await signOut({ redirect: false });

		// refetch the session to clear the data in this hook
		await refetch();

		window.location.href = '/auth/signin';
	}, [ refetch ] );

	return {
		session,
		isLoading,
		refetchSession: refetch,
		logOut
	};
}
